import React, { SyntheticEvent } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./Property.css";
import { DetailsRow, Details } from "./Details";
import { ExpandableText } from "./ExpandableText";
import { Footer } from "./Footer";
import { SavedRow, PropertyImage } from "./types";
import { PropertyAPI } from "./lib/property-api";
import { get_source } from "./lib/source";
import {
  dd,
  formatPrice,
  toS3Image,
  formatDecimalNumber,
  calculatePriceIncrease,
  conditionalAdd,
  conditionalAddIfTrue,
  conditionalAddPositiveNumber,
} from "./Utils";
import FancyImageGallery from "./FancyImageGallery";

interface Props {
  property_id: string;
}
function processPostalCity(d: string|null) : string {
  if(d === null || String(d).length === 0){
    return '';
  }
  d = d.replace(/^[\s]+/,'').replace(/[\s]+$/,'');
  return [String(d[0]).toUpperCase(),d.slice(1)].join('');
}
function presentUnitNumber(unitNumber: string|null) : string {
  if(unitNumber === null){
    return '';
  }
  if(typeof unitNumber === 'string'){
    if(unitNumber.match(/^#/)){
      return `Unit ${unitNumber}`;
    }
    if(unitNumber.length > 0){
      return `Unit #${unitNumber}`;
    }
  }
  return '';
}
function Property(props: Props) {
  const [isStaging, setIsStaging] = React.useState<boolean>(false);
  const [data, setData] = React.useState<SavedRow|null>(null);
  const [propertyID, setPropertyID] = React.useState<string>(props.property_id);
  const [beds, setBeds] = React.useState<number>(0);
  const [baths, setBaths] = React.useState<number>(0);
  const [halfBaths, setHalfBaths] = React.useState<number>(0);
  const [squareFeet, setSquareFeet] = React.useState<string>("");
  const [prettyPrice, setPrettyPrice] = React.useState<string>("");
  const [priceIncrease, setPriceIncrease] = React.useState<string | null>(null);
  const [rawPriceIncrease, setRawPriceIncrease] = React.useState<number>(0);
  const [oldPrice, setOldPrice] = React.useState<string | null>(null);
  const [address, setAddress] = React.useState<string>("");
  const [publicRemarks, setPublicRemarks] = React.useState<string | null>(null);
  const [listingKey, setListingKey] = React.useState<string>("");
  const [mlsStatus, setMlsStatus] = React.useState<string | null>(null);
  const [propertyDetails, setPropertyDetails] = React.useState<
    Array<DetailsRow>
  >([]);
  const [features, setFeatures] = React.useState<Array<DetailsRow>>([]);
  const [exteriorFeatures, setExteriorFeatures] = React.useState<
    Array<DetailsRow>
  >([]);
  const [error, setError] = React.useState<string | null>(null);
  const [images, setImages] = React.useState<PropertyImage[]>([]);
  const [propertyIsActive, setPropertyIsActive] =
    React.useState<boolean>(false);
  const [googleMapsLocation, setGoogleMapsLocation] = React.useState<
    string | null
  >(null);
  const [unitNumber, setUnitNumber] = React.useState<string | null>(null);
  const [showCopied, setShowCopied] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [shareableLinkIsLoading, setShareLinkIsLoading] =
    React.useState<boolean>(false);
  const [shareLink, setShareLink] = React.useState<URL | null>(null);
  const [api,_] = React.useState<PropertyAPI>(new PropertyAPI());

  function copyToClipboard(event: SyntheticEvent) {
    event.preventDefault();
    setShowCopied(false);
    try {
      navigator.clipboard
        .writeText(new URL(window.location.href).toString())
        .then(() => {
          setShowCopied(true);
          window.setTimeout(() => {
            setShowCopied(false);
          }, 2500);
        })
        .catch((reason) => {
          console.error("Unable to copy url to clipboard:", reason);
          setShareLinkIsLoading(false);
        });
      return;
    } catch (err) {
      console.error("Unable to copy to clipboard", err);
      setErrorMessage("Unable to copy URL to clipboard. You will have to copy and paste it manually");
      return;
    }
  }

  React.useEffect(() => {
    (async () => {
      setPropertyID(props.property_id);
      let tmp = await api.get_property_by_source(props.property_id,get_source());

      if(!tmp){
        return;
      }
      if (typeof tmp === "undefined") {
        setError("Unable to find the listing you were looking for.");
        return;
      }
      setError(null);
      dd({ propertyID, error, data, }, '<< data');
      //setPhotoCount(properties[key].Media.length);
      const img_list: Array<PropertyImage> = [];
      for (const media of tmp.Media) {
        try {
          img_list.push(toS3Image(media.MediaURL, media.Order, get_source()));
        } catch (err) {
          console.error(err);
        }
      }
      if (img_list.length) {
        img_list.sort((a: PropertyImage, b: PropertyImage) => a.order - b.order);
      }
      setImages(img_list);
      if (typeof tmp.PreviousListPrice !== "undefined") {
        setRawPriceIncrease(tmp.ListPrice - tmp.PreviousListPrice);
        setPriceIncrease(
          formatPrice(
            calculatePriceIncrease(tmp.ListPrice, tmp.PreviousListPrice)
          )
        );
        setOldPrice(formatPrice(tmp.PreviousListPrice));
      }
      if (typeof tmp.PublicRemarks !== "undefined") {
        setPublicRemarks(tmp.PublicRemarks);
      }
      if (typeof tmp.ListingKey !== "undefined") {
        if (String(tmp.ListingKey).match(/^KEY/)) {
          setListingKey(String(tmp.ListingKey).replace(/^KEY/, ""));
        } else {
          setListingKey(tmp.ListingKey);
        }
      }
      if (typeof tmp.MlsStatus !== "undefined") {
        setMlsStatus(tmp.MlsStatus);
        if (
          ["active", "pending"].includes(String(tmp.MlsStatus).toLowerCase())
        ) {
          setPropertyIsActive(true);
        } else {
          setPropertyIsActive(false);
        }
      } else {
        setMlsStatus(null);
      }
      let unit_number: string | null = presentUnitNumber(tmp.UnitNumber ?? null);
      setUnitNumber(unit_number);
      setSquareFeet(formatDecimalNumber(tmp.LotSizeSquareFeet));
      setBaths(tmp.BathroomsFull);
      setHalfBaths(tmp.BathroomsHalf);
      setBeds(tmp.BedroomsTotal);
      let street_number: string|null=null;
      if (
        typeof tmp.StreetNumber !== "undefined" &&
        String(tmp.StreetNumber) !== "undefined"
      ) {
        street_number = String(tmp.StreetNumber);
      }
      let postal_city: string|null = processPostalCity(tmp.PostalCity);
      if (unit_number) {
        setAddress(
          `${street_number !== null ? street_number + " " : ""}${tmp.StreetName} ${unit_number} ${tmp.StreetSuffix} ${postal_city}, ${tmp.StateOrProvince} ${tmp.PostalCode}`
        );
      } else {
        setAddress(
          `${street_number !== null ? street_number + " " : ""}${tmp.StreetName} ${tmp.StreetSuffix} ${postal_city}, ${tmp.StateOrProvince} ${tmp.PostalCode}`
        );
      }
      const str: string =
        "https://www.google.com/maps/place/" +
        encodeURIComponent(
          `${tmp.StreetNumber} ${tmp.StreetName} ${tmp.StreetSuffix} ${tmp.PostalCity}, ${tmp.StateOrProvince} ${tmp.PostalCode}`
        );

      setGoogleMapsLocation(str);
      setPrettyPrice(formatPrice(tmp.ListPrice));
      let feats: Array<DetailsRow> = [];
      let deets: Array<DetailsRow> = [];
      let ext: Array<DetailsRow> = [];
      conditionalAdd(tmp, "PropertyType", "Type", feats);
      conditionalAdd(tmp, "ParkingTotal", "Parking Spots", feats);
      conditionalAdd(tmp, "YearBuilt", "Year Built", feats);
      conditionalAdd(tmp, "LaundryFeatures", "Laundry Features", feats);
      conditionalAddIfTrue(tmp, "GarageYN", "Has Garage", feats);
      conditionalAdd(tmp, "Heating", 0, feats);
      conditionalAdd(tmp, "Cooling", 0, feats);
      setFeatures(feats);

      conditionalAddPositiveNumber(
        tmp,
        "BathroomsFull",
        "Full Bathrooms",
        deets
      );
      conditionalAddPositiveNumber(tmp, "BedroomsTotal", "Beds", deets);
      conditionalAdd(tmp, "InteriorFeatures", "Interior Features", deets);
      conditionalAdd(tmp, "Flooring", 0, deets);
      conditionalAdd(tmp, "LotSizeDimensions", "Lot Size Dimensions", deets);
      if (tmp.GarageYN === true) {
        conditionalAdd(tmp, "GarageSpaces", "Garage Spaces", deets);
      }
      conditionalAddIfTrue(tmp, "FireplaceYN", "Fireplace", deets);
      setPropertyDetails(deets);

      conditionalAdd(tmp, "PropertyType", "Property Type", ext);
      conditionalAdd(tmp, "ArchitecturalStyle", "Style", ext);
      conditionalAdd(tmp, "View", 0, ext);
      conditionalAdd(tmp, "ExteriorFeatures", "Exterior Features", ext);
      conditionalAdd(tmp, "Fencing", 0, ext);
      conditionalAdd(tmp, "FoundationDetails", "Foundation Details", ext);
      conditionalAdd(tmp, "WaterfrontFeatures", "Waterfront Features", ext);
      if (tmp.CarportYN === true) {
        conditionalAdd(tmp, "CarportSpaces", "Carport Spaces", ext);
      }
      setExteriorFeatures(ext);
      const url: URL = new URL(window.location.href);
      const is_staging: boolean = url.pathname.includes("/staging/");
      if (is_staging) {
        setIsStaging(true);
      }
      const back_disp: string =
        url.searchParams.get("backDisplayMode") ?? "show-active";
      const sort_mode: string = url.searchParams.get("sortMode") ?? "newest";
      const safe_url: URL = new URL(window.location.href);
      if (is_staging) {
        safe_url.pathname = "/staging/";
      } else {
        safe_url.pathname = "/mls-properties/";
      }
      safe_url.searchParams.set("displayMode", back_disp);
      safe_url.searchParams.set("sortMode", sort_mode);
      safe_url.searchParams.delete("backDisplayMode");
      setData(tmp);
      //setPhotoCount(data.Media.length);
      console.debug({ mlsStatus });
    })();
  }, []);//data, propertyID, error]);
  return (
    <div className="App container">
      <div
        className="content-area content-area-property container col-md-12 border-1"
        id="overview"
      >
        <div className="row">
          <div className="image-gallery b1-container col-md-9">
            {images.length === 0 && <div>This property has no images</div>}
            {images.length > 0 && (
              <FancyImageGallery property_id={propertyID} source={get_source()}/>
            )}
          </div>
          <div className="col-md-3 align-left container">
            <div className="row right-details">
              <div className="price-info-wrapper">
                {mlsStatus !== null &&
                  ["Active", "Pending"].includes(mlsStatus) && (
                    <div className="row">
                      <b className="for-sale">For Sale</b>
                    </div>
                  )}
                {mlsStatus === "Sold" && (
                  <div className="row">
                    <b className="for-sale">Sold</b>
                  </div>
                )}
                <div className="row">
                  <b className="for-sale-price">{prettyPrice}</b>
                </div>
                {propertyIsActive &&
                  priceIncrease &&
                  oldPrice &&
                  String(oldPrice) !== "-$1" && (
                    <>
                      {rawPriceIncrease > 0 && (
                        <>
                          <div className="row">
                            <b className="for-sale-price-increase">
                              Price Increased: {priceIncrease}
                            </b>
                          </div>
                        </>
                      )}
                      {rawPriceIncrease < 0 && (
                        <>
                          <div className="row">
                            <b className="for-sale-price-increase">
                              Price Decreased: {priceIncrease}
                            </b>
                          </div>
                        </>
                      )}
                      <div className="row">
                        <b className="for-sale-price-previously">
                          Previously: {oldPrice}
                        </b>
                      </div>
                    </>
                  )}
              </div>

              {propertyIsActive && (
                <div className="row cta-rat-wrapper">
                  <a className="cta-rat" href="/contact-us">
                    Request A Tour
                  </a>
                </div>
              )}
              {propertyIsActive === false && (
                <div className="row cta-rat-wrapper">
                  <a
                    className="cta-rat"
                    href={isStaging ? "/staging/" : "/mls-properties/"}
                  >
                    See more properties
                  </a>
                </div>
              )}
            </div>
            <div className="row right-details margin-top-separator price-info-wrapper">
              <div className="row">
                {shareableLinkIsLoading === true && (
                  <button
                    className={
                      "share-button-generic pslh-share share-link-icons share-link-loading "
                    }
                    onClick={(e: SyntheticEvent) => {
                      e.preventDefault();
                    }}
                  >
                    Loading...
                  </button>
                )}
                {shareableLinkIsLoading === false && shareLink === null && (
                  <button
                    className={
                      "share-button-generic pslh-share share-link-icons "
                    }
                    onClick={(e: SyntheticEvent) => {
                      e.preventDefault();
                      setShareLink(null);
                      setShareLinkIsLoading(true);
                      window.setTimeout(() => {
                        setShareLinkIsLoading(false);
                        setShareLink(new URL(window.location.href));
                      }, 2_000);
                    }}
                  >
                    Get shareable link
                  </button>
                )}
                {shareableLinkIsLoading === false && shareLink !== null && (
                  <div>
                    <div className="pslh-copy-inputs-wrapper share-modal-buttons">
                      <div className="pslh-minify-container">
                        <input
                          className="share-link"
                          type="text"
                          value={shareLink !== null ? shareLink.toString() : ""}
                        ></input>
                        <button
                          className="share-button-generic pslh-copy-btn"
                          onClick={copyToClipboard}
                        >
                          Copy
                        </button>
                        {showCopied && (
                          <b className="copied-notification-text">Copied!</b>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="p-error">{errorMessage}</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="row">
                <span className="">
                  See more properties by Lombardo Homes &amp; Estates
                </span>
              </div>
            </div>
          </div>
          <div className="part-two col-md-9">
            <div className="property-details b1-container col-md-12">
              <div className="property-details-contents">
                <div className="zsecond-details-row ">
                  <span className="app-address-line zsdr-detail">
                    {address}
                  </span>
                </div>
                <div className="zsecond-details-row ">
                  <span className="zsdr-detail app-mls-line">
                    MLS <span className="app-listing-key">#{listingKey}</span>
                  </span>
                </div>
              </div>
            </div>
            {/** TODO
            <div className="text-center image-gallery-link">
              <a href="#image-gallery" onClick={() => setPage('image-gallery')}>View All {photoCount} Photos</a>
            </div>
            */}
            <div className="property-details b1-container col-md-12">
              <div className="property-details-contents">
                <hr></hr>
                <div className="d-flex flex-row justify-content-between">
                  <div className="second-details-row">
                    <b className="sdr-detail">Beds</b>
                    <br></br>
                    {beds} Bed{beds > 1 ? "s" : ""}
                  </div>
                  {baths > 0 && (
                    <div className="second-details-row">
                      <b className="sdr-detail">Baths</b>
                      <br></br>
                      {baths} ({baths} Full){" "}
                      {halfBaths > 0 ? "(" + halfBaths + " half)" : ""}
                    </div>
                  )}
                  <div className="second-details-row">
                    {squareFeet !== "-1" && squareFeet !== null && Number(squareFeet) > 0 && (
                      <>
                        <b className="sdr-detail">Size</b>
                        <br></br>
                        {squareFeet} sqft
                      </>
                    )}
                  </div>
                  {googleMapsLocation !== null && (
                    <div className="second-details-row">
                      <a href={googleMapsLocation} about="_blank">
                        View on Google Maps
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <hr></hr>
            </div>
            {publicRemarks && (
              <>
                <div className="property-details b1-container col-md-12">
                  <div className="property-details-contents">
                    <div className="zsecond-details-row ">
                      <span className="zsdr-detail">
                        <ExpandableText text={publicRemarks} maxLength={512} />
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          {/** -- End part-two -- */}
          <div className="part-three">
            <Details title={"Property Details"} details={propertyDetails} />
            <Details title={"Features"} details={features} />
            <Details title={"Exterior Features"} details={exteriorFeatures} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Property;
